import { createEffect, untrack } from "solid-js";
import { fromActorRef } from "@fleet/state-machines/solid";
import { sessionActor } from "@/lib/session-machine";
import { useQuery } from "@tanstack/solid-query";
import { queryKeys } from "@stockpilot/shared";
import { cloneSession } from "@/lib/auth";
import { readActiveAccountSync } from "@/lib/accounts";
import { api } from "@/lib/api";
import { SESSION_STALE_TIME_MS } from "@/lib/constants";

interface AppError {
  message?: string;
  status?: number;
}

function isAppError(error: unknown): error is AppError {
  return typeof error === "object" && error !== null && ("message" in error || "status" in error);
}

/**
 * Singleton component that synchronizes the TanStack Query session state
 * with the XState session actor.
 *
 * This MUST only be rendered once in the app (usually in AppProviders)
 * to prevent infinite reactivity loops.
 */
export function SessionBridge() {
  const snapshot = fromActorRef(sessionActor);

  const query = useQuery(() => ({
    queryKey: queryKeys.auth.session(),
    queryFn: async () => {
      const result = await api.GET("/api/auth/get-session", {
        params: { query: { jwt: true } },
      });
      return result.data ?? null;
    },
    // Only fetch if the machine is done priming (local storage check)
    enabled: !untrack(() => snapshot().value === "priming"),
    staleTime: SESSION_STALE_TIME_MS,
  }));

  createEffect(() => {
    // 1. Only sync if the query has finished its first load
    if (!query.isPending) {
      // Multi-account: honor an explicit GUEST choice. The backend cookie may
      // still authenticate a real account, but reviving that session here would
      // flip the app back to the real identity (and swap the active PowerSync DB
      // away from the guest). The real session is re-primed on switching back.
      if (readActiveAccountSync()?.kind === "guest") {
        return;
      }
      // P23 / CC-60 · I1 — `data` is the only field of the solid-query proxy that
      // SUSPENDS (`state.data === undefined` → `queryResource()`), and this
      // component is mounted in `AppProviders`, ABOVE the
      // `<RouterProvider>`: reading it without a guard leaves the ENTIRE app blank
      // behind `__root`'s `<Suspense fallback={null}>`.
      //
      // The ERROR exit is DEFENSIVE, not a fix for an observed failure.
      // What was MEASURED (probe on this same effect, server running):
      //
      //   · A 401 from `get-session` does NOT produce an error state. The `queryFn`
      //     uses openapi-fetch and does `return result.data ?? null`, so it
      //     NEVER throws: the query ends with `status: "success"` and
      //     `data === null`. And with `state.data === null` (≠ `undefined`) the
      //     proxy returns `latest?.data` WITHOUT suspending.
      //   · `isError` is reached only when the `fetch` actually blows up
      //     (API unreachable → «Failed to fetch»), not because of an HTTP status.
      //   · In THAT scenario, measured with and without this early exit, the
      //     primed account survives just the same and the profile does not fall to «Guest Mode»: the
      //     per-tab view is resolved from the registry, not from the machine.
      //
      // It is kept because sending `SERVER_REPLY(session: null)` from a state in which
      // `data` could NOT be read asserts something unknown. The one that confirms
      // guest mode is `runSessionCheck` in the
      // `beforeLoad` of `_app/route.tsx`, not this bridge.
      if (!query.isSuccess) {
        return;
      }
      const sessionData = query.data ? cloneSession(query.data) : null;

      // 2. Prevent unnecessary updates using robust manual comparison.
      // Use untrack to avoid dependency on the machine state while reading it.
      const current = untrack(() => snapshot().context.session);
      const currentErr = untrack(() => {
        const err = snapshot().context.error;
        return isAppError(err) ? err : null;
      });

      const isDataSame =
        (!sessionData && !current) || (sessionData && sessionData.user.id === current?.user.id);

      // After the `isSuccess` guard, the error is null by definition; the
      // comparison remains so a PREVIOUS machine error is cleared when a good
      // response arrives.
      const isErrorSame = !currentErr;

      if (isDataSame && isErrorSame) {
        return;
      }

      console.log("[SessionBridge] Syncing session to machine:", {
        hasData: Boolean(sessionData),
        hasError: false,
      });

      sessionActor.send({
        type: "SERVER_REPLY",
        session: sessionData,
        error: null,
      });
    }
  });

  return null;
}
